// Portfolio Administration v1.0 PR 5 — Engagements tab on the Local
// Enforcer registry detail page. Cross-loan list of EnforcerEngagement
// rows for this enforcer (uses GET /local-enforcers/{id}/engagements
// from PR 4b). Row click navigates to the loan's Defaults &amp; Workout
// tab deep-linked to that engagement's case.

import { useNavigate } from "react-router-dom";
import { Card, Table, TBody, TD, TH, THead, TR } from "@/design-system/components/ui";
import { AlertTriangle } from "lucide-react";
import { useEnforcerEngagementsForEnforcer } from "@/hooks/portfolioAdmin/useRegistries";
import { formatCalendarDate, useDateFormatter } from "@/utils/datetime";

export function EnforcerEngagementsTab({
  enforcerId,
}: {
  enforcerId: string;
}) {
  const navigate = useNavigate();
  const q = useEnforcerEngagementsForEnforcer(enforcerId);
  const rows = q.data ?? [];
  const { date } = useDateFormatter();

  const active = rows.filter(
    (r) => r.status === "active" || r.status === "in-progress",
  );
  const reportsOverdue = rows.filter((r) => {
    if (!r.nextReportDue) return false;
    return new Date(r.nextReportDue).getTime() < Date.now();
  });

  return (
    <div className="flex flex-col gap-space-3" data-testid="enforcer-engagements-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
        <RollupCard label="Active engagements" value={active.length} />
        <RollupCard
          label="Reports overdue"
          value={reportsOverdue.length}
          tone={reportsOverdue.length > 0 ? "warn" : "neutral"}
        />
        <RollupCard
          label="Total engagements (lifetime)"
          value={rows.length}
        />
      </div>
      <Card className="flex flex-col gap-space-2">
        <h3 className="font-display font-bold text-h3 m-0">All engagements</h3>
        {q.isLoading ? (
          <p className="text-small text-gray-mid italic m-0">
            Loading engagements…
          </p>
        ) : rows.length === 0 ? (
          <p className="text-small text-gray-mid italic m-0">
            No engagements yet for this local enforcer.
          </p>
        ) : (
          <Table data-testid="enforcer-engagements-table">
            <THead>
              <TR>
                <TH className="text-white-pure">Loan</TH>
                <TH className="text-white-pure">Type</TH>
                <TH className="text-white-pure">Status</TH>
                <TH className="text-white-pure">Start</TH>
                <TH className="text-white-pure">Expected end</TH>
                <TH className="text-white-pure">Fee</TH>
                <TH className="text-white-pure">Next report due</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((r) => {
                const overdue =
                  r.nextReportDue &&
                  new Date(r.nextReportDue).getTime() < Date.now();
                return (
                  <TR
                    key={r.id}
                    className="cursor-pointer"
                    data-testid={`enforcer-engagement-row-${r.id}`}
                    onClick={() =>
                      navigate(
                        `/portfolio-admin/loans/${r.loanId}?tab=defaults${r.defaultCaseId ? `&case=${r.defaultCaseId}` : ""}`,
                      )
                    }
                  >
                    <TD>
                      <span className="text-gold-shadow hover:underline">
                        {r.loanId.slice(0, 8)}…
                      </span>
                    </TD>
                    <TD>{r.engagementType}</TD>
                    <TD>{r.status}</TD>
                    <TD>
                      {r.engagementStartDate ? formatCalendarDate(r.engagementStartDate) : "—"}
                    </TD>
                    <TD>
                      {r.expectedEndDate ? formatCalendarDate(r.expectedEndDate) : "—"}
                    </TD>
                    <TD>
                      {r.feeAmount != null
                        ? `$${r.feeAmount.toLocaleString()}`
                        : "—"}
                    </TD>
                    <TD>
                      {r.nextReportDue ? (
                        <span
                          className={
                            overdue
                              ? "inline-flex items-center gap-space-1 text-status-error"
                              : ""
                          }
                        >
                          {overdue ? (
                            <AlertTriangle size={14} aria-hidden="true" />
                          ) : null}
                          {date(r.nextReportDue)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function RollupCard({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number;
  tone?: "neutral" | "warn";
}) {
  return (
    <Card className="flex flex-col gap-space-1">
      <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
        {label}
      </span>
      <span
        className={
          tone === "warn" && value > 0
            ? "font-display font-bold text-h2 text-status-error"
            : "font-display font-bold text-h2"
        }
      >
        {value}
      </span>
    </Card>
  );
}
